import { Avatar, Divider, Grid, Stack, Typography } from '@mui/material';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary } from 'Hooks';
import { differenceInDays, format } from 'date-fns';
import { Equipment_Order_STATUSES, IEquipmentPrevOrder } from 'API';
import { Status } from '../../Status';
import { useNavigate } from 'react-router-dom';
import { Copy, LeftArrow, PurpleOutlineWarningIcon } from 'Images';
import { OneOrTwoButtons } from 'Components/OneOrTwoButtons';
import { shortDateFormat, shorterDateFormat } from 'Config';

interface IOrderCard {
  titles: string[];
  item: IEquipmentPrevOrder;
  onShowOrderDetailsClick: (clickedOrder: any) => void;
}

export const OrderCard = ({
  item,
  onShowOrderDetailsClick,
  titles,
}: IOrderCard) => {
  const dictionary = useDictionary(Dictionary, DICTIONARY.Equipment);
  const navigate = useNavigate();

  const daysDiff = differenceInDays(
    new Date(item.endDate),
    new Date(item.startDate)
  );
  const monthsDiff = Math.ceil(daysDiff / 30);
  const orderID = item.requestNumber || item.internetRequestNumber;

  const handleNavigate = () => {
    navigate(`/Equipment/${orderID}`);
  };

  return (
    <Grid
      container
      item
      xs={12}
      alignItems={'center'}
      sx={{
        marginBottom: '16px',
        cursor: 'pointer',
        border: '1px solid',
        borderColor: 'primary.shade_100',
        boxShadow: '-2px -2px 12px 0px rgba(8, 30, 84, 0.04)',
        borderRadius: '16px',
      }}
    >
      <Grid
        container
        item
        sx={{
          background: '#FFF',
          padding: '16px 16px 24px 16px',
          alignContent: 'space-between',
          borderRadius: '16px 16px 0 0',
        }}
      >
        <Grid container item justifyContent={'space-between'}>
          <Grid item xs={5}>
            <Typography variant='body6'>{titles[0]}</Typography>
            {item.createDate && (
              <Typography variant='body2'>
                {format(new Date(item.createDate), shortDateFormat)}
              </Typography>
            )}
          </Grid>
          <Grid item xs={7} mb={2}>
            <Typography variant='body6'>{titles[1]}</Typography>
            <Typography variant='body2'>
              {item.requestNumber
                ? item.requestNumber
                : dictionary.orderDialog.willBeSettledLater}
            </Typography>
          </Grid>
          <Grid item xs={5}>
            <Typography variant='body6'>{titles[2]}</Typography>
            {item.orderItemsCount && (
              <Typography variant='body2'>{item.orderItemsCount}</Typography>
            )}
          </Grid>
          <Grid item xs={7}>
            <Typography variant='body6' display='block'>
              {titles[3]}
            </Typography>
            <Status
              status={item.currentStatus}
              statusLabel={item.currentStatusLabel}
              endDate={item.endDate}
            />
          </Grid>
        </Grid>

        {!!item.adminIsrId && (
          <Stack
            alignItems={'center'}
            gap={'6px'}
            flexDirection={'row'}
            mt='20px'
          >
            <Avatar
              sx={{ width: '20px', height: '20px' }}
              alt={Dictionary[DICTIONARY.General].warning}
              src={PurpleOutlineWarningIcon}
            />
            <Typography variant='body3' role='note'>
              {Dictionary[DICTIONARY.Applies].openedByWing}
            </Typography>
          </Stack>
        )}

        <Divider
          aria-hidden='true'
          flexItem
          sx={{
            margin: '20px 0',
            borderColor: 'primary.shade_100',
            width: '100%',
          }}
        />
        <Stack flexDirection='row' alignItems='center'>
          <Avatar
            src={PurpleOutlineWarningIcon}
            sx={{ width: '16px', height: '16px', mr: 1 }}
          />
          <Typography variant='body6' sx={{ mr: '6px' }}>
            {dictionary.RecentOrders.orderPeriodInfo(
              monthsDiff,
              format(new Date(item.endDate), shorterDateFormat)
            )}
          </Typography>
        </Stack>
      </Grid>
      <OneOrTwoButtons
        cardVariant
        leftButton={{
          label: dictionary.RecentOrders.moreInfo,
          icon: LeftArrow,
          onClick: handleNavigate,
        }}
        rightButton={
          item.currentStatus > Equipment_Order_STATUSES.takenCareByDept4
            ? {
                label: dictionary.RecentOrders.duplicateOrder,
                icon: Copy,
                onClick: () => {
                  navigate(
                    `${dictionary.RecentOrders.duplicateLink}${orderID}`
                  );
                },
              }
            : undefined
        }
      />
    </Grid>
  );
};
